import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Alert, AlertTitle, Box, Button, Divider, Stack, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

import InfoIcon from '@mui/icons-material/Info';
import KbbLogo from 'assets/logos/kbb-logo.png';
import InfoTooltipIcon from 'components/InfoTooltipIcon';

import { vehicleSelectors } from 'app-components/flux/vehicle';

import { accountsSelectors } from 'app-components/flux/accounts';
import { businessSelectors } from 'app-components/flux/business';
import { entitlementsSelectors } from 'app-components/flux/entitlements';
import { vehicleFormHooks } from 'app-components/flux/vehicle/vehicleForm';
import { VehicleConditionLabel, VehiclePriceTypeLabel } from 'app-components/flux/vehicle/vehicleTypes';
import clsx from 'clsx';
import AccountLink from 'components/QuickenControls/AccountField/AccountLink';
import { useAppSelector } from 'hooks/redux';
import { BusinessPurposeSelect, NameInput } from '../VehicleFields';
import { BUILD_FULL_VEHICLE_NAME, KBB_VALUE_DESCRIPTION, VEHICLE_CONDITION_DEFINITIONS } from '../utils';
import useFormatAsCurrency from 'hooks/useFormatAsCurrency';

const useStyles = makeStyles()((theme) => ({
  mainTitle: {
    marginBottom: theme.spacing(1),
  },
  reviewDetailsSection: {
    display: 'flex',
    flexDirection: 'row',
    border: `1px solid ${theme.palette.border.main}`,
    borderRadius: 16,
    backgroundColor: theme.palette.background.default,
    marginBottom: theme.spacing(3),

    '& >:first-child': {
      borderRight: `1px solid ${theme.palette.border.main}`,
    },
  },
  reviewDetailsHalf: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    padding: theme.spacing(1.5, 2, 1.5, 2),
  },
  reviewName: {
    marginBottom: theme.spacing(1.5),
  },
  reviewOverallRating: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(0.5),
  },
  tradeInValue: {
    display: 'flex',
    alignItems: 'center',
  },
  confirmDetailsTitle: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    gap: theme.spacing(1),
  },
  confirmDetailsSection: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
  },
  confirmDetailsPersonal: {
    gap: theme.spacing(3),
  },
  confirmDetailsBusiness: {
    gap: theme.spacing(2),
  },
  confirmDetailsThird: {
    flex: 1,
    minWidth: 0,
  },
  confirmDetailsLast: {
    flex: 0.5,
  },
  horizontalBorder: {
    flexGrow: 1,
    height: '1px',
    backgroundColor: theme.palette.border.light,
  },
  kbbLogo: {
    height: 74,
    width: 'auto',
  },
  tooltip: {
    color: theme.palette.icon.light,
    marginLeft: theme.spacing(0.75),
    '& .MuiSvgIcon-root': {
      fontSize: 16,
    },
  },
  kbbNote: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(3),
  },
  reviewAlertContainer: {
    padding: theme.spacing(1, 2),
    '& .MuiAlert-message': {
      flex: 1,
      padding: 0,
    },
    '& .MuiAlert-icon': {
      padding: 0,
      marginRight: theme.spacing(1),
    },
    '& .MuiAlertTitle-root': {
      margin: 0,
    },
  },
  reviewAlertTop: {
    marginBottom: theme.spacing(2),
  },
  reviewAlertContent: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  addManualButton: {
    padding: theme.spacing(1),
    textTransform: 'none',
  },
}));

const ReviewDropdowns = ({ inEditMode }: { inEditMode?: boolean }) => {
  const { classes } = useStyles();

  const { onValueChange: setAccountName } = vehicleFormHooks.useField('name');
  const { value: linkTo, onValueChange: setLinkTo } = vehicleFormHooks.useField('linkTo');
  const vehiclePreview = useAppSelector(vehicleSelectors.getVehiclePreview);
  const manualVehicleAccountsById = useAppSelector(accountsSelectors.getManualVehicleAccounts);

  // QBP
  const businessEnabled: boolean = useAppSelector(entitlementsSelectors.getBusinessEnabled);
  const showBusinessSelect = useAppSelector(
    (state) => businessSelectors.getBusinessList(state)?.size > 0 && businessEnabled,
  );

  if (!vehiclePreview) return null;

  const handleLinkToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const accountId = e.target.value as string;
    setLinkTo(accountId);

    const account = manualVehicleAccountsById.get(accountId);
    const accountName = account?.name;
    if (accountId && accountId !== '0' && accountName) {
      setAccountName(accountName);
    } else {
      setAccountName(
        BUILD_FULL_VEHICLE_NAME(
          vehiclePreview.year,
          vehiclePreview.makeName,
          vehiclePreview.modelName,
          vehiclePreview.trimName,
        ),
      );
    }
  };

  return (
    <Box
      className={clsx(
        classes.confirmDetailsSection,
        !inEditMode ? (showBusinessSelect ? classes.confirmDetailsBusiness : classes.confirmDetailsPersonal) : null,
      )}
    >
      <Box className={classes.confirmDetailsThird}>
        <NameInput linkTo={linkTo} />
      </Box>
      {showBusinessSelect && (
        <Box className={!inEditMode ? classes.confirmDetailsThird : classes.confirmDetailsLast}>
          <BusinessPurposeSelect />
        </Box>
      )}
      {!inEditMode ? (
        <>
          <Box className={classes.confirmDetailsThird}>
            <AccountLink value={linkTo} onChange={handleLinkToChange} options={manualVehicleAccountsById} />
          </Box>
        </>
      ) : null}
    </Box>
  );
};

type ReviewStepProps = {
  switchUserPath: () => void;
  inEditMode?: boolean;
};

export const ReviewStep = (props: ReviewStepProps) => {
  const { classes } = useStyles();
  const { switchUserPath, inEditMode } = props;

  const vehiclePreview = useAppSelector(vehicleSelectors.getVehiclePreview);

  const formatAsCurrency = useFormatAsCurrency();

  if (!vehiclePreview) return;

  const { year, makeName, modelName, trimName, zipCode, miles, valuation, priceType, condition } = vehiclePreview;
  const fullVehicleName = BUILD_FULL_VEHICLE_NAME(year, makeName, modelName, trimName);

  const zeroValuation = valuation === 0;

  return (
    <>
      {zeroValuation && (
        <Alert
          severity='info'
          icon={<InfoIcon />}
          className={clsx(classes.reviewAlertContainer, classes.reviewAlertTop)}
        >
          <Box className={classes.reviewAlertContent}>
            <Box sx={{ flex: 1 }}>
              <AlertTitle>
                <Typography variant='bodyMedium'>No value was found for this vehicle.</Typography>
              </AlertTitle>
              <Typography variant='paragraphRegular'>
                You can choose to continue linking here, or add your vehicle manually and enter your own value.
              </Typography>
            </Box>
            <Button className={classes.addManualButton} endIcon={<ArrowForwardIcon />} onClick={switchUserPath}>
              {inEditMode ? 'Edit manually' : 'Add manually'}
            </Button>
          </Box>
        </Alert>
      )}
      <Typography variant='headlineRegular'>Review vehicle value</Typography>
      <Typography className={classes.mainTitle} variant='paragraphRegular'>
        Typical configuration is being used. Your vehicle&apos;s value may be higher or lower depending on the options.
      </Typography>
      <Box className={classes.reviewDetailsSection}>
        <Box className={classes.reviewDetailsHalf}>
          <Typography variant='titleMedium' className={classes.reviewName}>
            {fullVehicleName}
          </Typography>
          <Stack mt='auto'>
            <Box className={classes.reviewOverallRating}>
              <Typography variant='bodyRegular'>{`Overall Rating: ${VehicleConditionLabel[condition]}`}</Typography>
              <InfoTooltipIcon
                className={classes.tooltip}
                tooltipPlacement='right'
                tooltipHTML={
                  <Typography variant='captionRegular'>{VEHICLE_CONDITION_DEFINITIONS[condition]}</Typography>
                }
              />
            </Box>
            <Box display='flex' flexDirection='row' mt={'auto'}>
              <Typography variant='captionRegular'>{`ZIP Code ${zipCode}`}</Typography>
              <Divider orientation='vertical' flexItem sx={{ mx: 1 }} />
              <Typography variant='captionRegular'>{`${Number(miles).toLocaleString()} miles`}</Typography>
            </Box>
          </Stack>
        </Box>
        <Box className={classes.reviewDetailsHalf}>
          <Typography variant='titleMedium' color='primary'>
            {`Kelley Blue Book® ${VehiclePriceTypeLabel[priceType]}:`}
          </Typography>
          <Box className={classes.tradeInValue}>
            <Typography variant='displayMedium'>
              {valuation > 0 ? formatAsCurrency(valuation).replace(/\.00$/, '') : formatAsCurrency(valuation)}
            </Typography>
            <InfoTooltipIcon
              className={classes.tooltip}
              tooltipPlacement='right'
              tooltipHTML={<Typography variant='captionRegular'>{KBB_VALUE_DESCRIPTION[priceType]}</Typography>}
            />
          </Box>
          <Typography variant='captionRegular' mt={'auto'}>
            {`Value valid as of ${DateTime.local().toFormat('MM/dd/yyyy')}`}
          </Typography>
        </Box>
      </Box>
      <Box className={classes.confirmDetailsTitle}>
        <Typography variant='titleMedium'>Confirm details</Typography>
        <Box className={classes.horizontalBorder} />
      </Box>
      <ReviewDropdowns inEditMode={inEditMode} />
      <Box className={classes.kbbNote}>
        <Typography variant='captionRegular' color='textSecondary'>
          {`©${DateTime.local().year} Kelley Blue Book Co., Inc. All rights reserved. 
            As of ${DateTime.local().toFormat('MM/dd/yyyy')} Edition for ${zipCode}. 
            Values and pricing are the opinions of Kelley Blue Book, and the actual price of a vehicle may vary. 
            The value and pricing information displayed for a particular vehicle is based upon the specification, 
            mileage and/or condition information provided by the person generating this report. 
            Kelley Blue Book assumes no responsibility for errors or omissions.`}
        </Typography>
        <Box>
          <img src={KbbLogo} alt={'Kelley Blue Book logo'} className={classes.kbbLogo} />
        </Box>
      </Box>
      {!zeroValuation && (
        <Alert severity='info' icon={false} className={classes.reviewAlertContainer}>
          <Box className={classes.reviewAlertContent}>
            <Box sx={{ flex: 1 }}>
              <AlertTitle>
                <Typography variant='bodyMedium'>Value doesn&apos;t look right?</Typography>
              </AlertTitle>
              <Typography variant='paragraphRegular'>Add your vehicle as a manual account instead.</Typography>
            </Box>
            <Button className={classes.addManualButton} endIcon={<ArrowForwardIcon />} onClick={switchUserPath}>
              {inEditMode ? 'Edit manually' : 'Add manually'}
            </Button>
          </Box>
        </Alert>
      )}
    </>
  );
};
